import type { PositionSource, TripStatus } from '@chukua/common';
import { api } from '../../shared/shared.api';

/** `GET /trips/:id` as the trip page reads it: the trip with the names its header shows. */
export interface TripPageData {
  id: string;
  number: string;
  jobId: string;
  legType: string;
  status: TripStatus;
  originZoneCode: string | null;
  destinationZoneCode: string | null;
  windowStart: string | null;
  windowEnd: string | null;
  job: { id: string; number: string; status: string };
  customerName: string | null;
  truck: { id: string; registration: string } | null;
  trailer: { id: string; registration: string } | null;
  driver: { id: string; name: string } | null;
}

export function getTripPage(tripId: string): Promise<TripPageData> {
  return api<TripPageData>(`/trips/${tripId}`);
}

export interface TrackPoint {
  id: string;
  source: PositionSource;
  lat: number;
  lng: number;
  accuracyM: number | null;
  speedMps: number | null;
  headingDeg: number | null;
  deviceTime: string;
  serverTime: string;
}

/** `GET /trips/:id/track`: the phone's and the tracker's fixes between dispatch and close-out, oldest first. */
export interface TripTrack {
  tripId: string;
  truckId: string | null;
  isThinned: boolean;
  points: TrackPoint[];
  latest: TrackPoint | null;
}

export function getTripTrack(tripId: string): Promise<TripTrack> {
  return api<TripTrack>(`/trips/${tripId}/track`);
}

export interface TripLogEntry {
  id: string;
  eventType: string;
  source: string;
  deviceTime: string;
  serverTime: string;
  correctsEventId: string | null;
}

/** `GET /trips/:id/events`: the trip's event log, oldest first. */
export function getTripEvents(tripId: string): Promise<{ tripId: string; events: TripLogEntry[] }> {
  return api<{ tripId: string; events: TripLogEntry[] }>(`/trips/${tripId}/events`);
}

export type EtaDeadlineVerdict =
  | { status: 'not_applicable' | 'no_deadline' | 'reached' | 'no_estimate' }
  | { status: 'on_time' | 'at_risk'; arrivalAt: string; deadlineAt: string; slackSeconds: number };

/** A plan stop in the newest recalculation: a reached stop carries no estimate; variance is signed seconds, late positive. */
export type EtaStop =
  { position: number; name: string; isReached: true } | { position: number; name: string; isReached: false; varianceSeconds: number | null };

/** `GET /trips/:id/eta`: only the part the header reads, the deadline verdicts and each stop's variance. */
export interface TripEta {
  status: 'estimated' | 'no_plan' | 'awaiting_position' | 'not_in_progress';
  deadline: { delivery: EtaDeadlineVerdict; emptyReturn: EtaDeadlineVerdict } | null;
  recalculation: { stops: EtaStop[] } | null;
}

export function getTripEta(tripId: string): Promise<TripEta> {
  return api<TripEta>(`/trips/${tripId}/eta`);
}
